// App.jsx
import { useState, useEffect } from 'react';
import { fetchProgramData } from './utils/sheets';
import Header from './components/Header';
import AgeGroupSection from './components/AgeGroupSection';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

function App() {
  const [programData, setProgramData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayMonth, setDisplayMonth] = useState('all'); // 'all', 'april', 'may', 'june'
  
  useEffect(() => {
    const loadProgramData = async () => {
      try {
        setLoading(true);
        const data = await fetchProgramData();
        setProgramData(data);
        setError(null);
      } catch (err) {
        setError('Failed to load program data. Please try again later.');
        console.error('Error loading program data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadProgramData();
  }, []);

  // Optional: Filter programs by month if you want to add this feature later
  const filterProgramsByMonth = (programs) => {
    if (displayMonth === 'all') return programs;
    
    return programs.filter(program => {
      return program.dates.some(date => 
        date.toLowerCase().includes(displayMonth.toLowerCase())
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-100">
        <div className="container mx-auto px-3 md:px-4 py-6 md:py-8 max-w-3xl">
          {/* Header Skeleton */}
          <div className="mb-8 text-center">
            <div className="flex flex-col items-center mb-4">
              <div className="h-32 w-32 bg-gray-200 rounded mb-4 animate-pulse"></div>
              <div className="h-40 w-40 bg-gray-200 rounded-full animate-pulse"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-2 animate-pulse"></div>
            <div className="h-6 bg-gray-200 rounded w-1/4 mx-auto mb-4 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto animate-pulse"></div>
          </div>

          {/* Programs Skeleton */}
          <div className="space-y-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg p-4 md:p-6 bg-gray-100 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="grid gap-6 lg:grid-cols-2">
                  {[1, 2].map((j) => (
                    <div key={j} className="bg-white rounded-lg p-4 md:p-5">
                      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                      <div className="h-10 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-2.5 px-6 rounded-lg transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  if (!programData) {
    return null;
  }
  
  const { siteInfo, ipLevel2, ageGroups } = programData;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-100">
      <div className="container mx-auto px-3 md:px-4 py-6 md:py-8 max-w-3xl">
        <Header
          title={siteInfo.title}
          subtitle={siteInfo.subtitle}
          logo={siteInfo.logo}
        />
        
        {/* Optional Month Filter - Uncomment if needed 
        <div className="flex justify-center mb-8 space-x-4">
          <button 
            className={`px-4 py-2 rounded-full ${displayMonth === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setDisplayMonth('all')}
          >
            All
          </button>
          <button 
            className={`px-4 py-2 rounded-full ${displayMonth === 'april' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setDisplayMonth('april')}
          >
            April
          </button>
          <button 
            className={`px-4 py-2 rounded-full ${displayMonth === 'may' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setDisplayMonth('may')}
          >
            May
          </button>
          <button 
            className={`px-4 py-2 rounded-full ${displayMonth === 'june' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setDisplayMonth('june')}
          >
            June
          </button>
        </div>
        */}
        
        <main className="space-y-8 md:space-y-12">
          {/* IP Level 2 Section - Only show if programs exist */}
          {ipLevel2.programs.length > 0 && (
            <AgeGroupSection
              key={ipLevel2.id}
              ageGroup={ipLevel2}
              programs={filterProgramsByMonth(ipLevel2.programs)}
            />
          )}

          {/* Main Intuition Process Section - Only show if at least one age group has programs */}
          {ageGroups.some(group => group.programs.length > 0) && (
            <div className="bg-white rounded-lg p-4 md:p-6 shadow-md">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-blue-800 mb-6 md:mb-8">Intuition Process</h2>
              <div className="space-y-8 md:space-y-12">
                {ageGroups
                  .filter(ageGroup => ageGroup.programs.length > 0)
                  .map((ageGroup) => (
                    <AgeGroupSection
                      key={ageGroup.id}
                      ageGroup={ageGroup}
                      programs={filterProgramsByMonth(ageGroup.programs)}
                    />
                  ))}
              </div>
            </div>
          )}
        </main>
        
        <Footer />
      </div>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}

export default App;